import { compare, isNegative } from '@aeris/money';
const toResolvedList = (list) => ({
    id: list.id,
    code: list.code,
    name: list.name,
    currency: list.currency,
    isDefault: list.isDefault,
});
/**
 * Half-open: `[validFrom, validTo)`.
 *
 * Rule 2. Closed at the end instead would make the instant a promotion ends
 * and its successor begins belong to both.
 */
const windowState = (from, to, at) => {
    if (from !== null && at.getTime() < from.getTime())
        return 'not-yet-valid';
    if (to !== null && at.getTime() >= to.getTime())
        return 'expired';
    return 'in-force';
};
/** Rule 3: the item's own window when it states one, otherwise the list's. */
const governingWindow = (item, list) => {
    if (item.validFrom !== null || item.validTo !== null) {
        return { from: item.validFrom, to: item.validTo, source: 'item' };
    }
    if (list.validFrom !== null || list.validTo !== null) {
        return { from: list.validFrom, to: list.validTo, source: 'list' };
    }
    return { from: null, to: null, source: 'none' };
};
/**
 * Rule 4, and the tie-break.
 *
 * Highest minimum first. On an equal minimum — impossible in stored data, see
 * the header — the lower price, then the id, so the order is total and the
 * answer is the same everywhere.
 */
const mostSpecificFirst = (left, right) => {
    const byMinimum = compare(right.minQuantity, left.minQuantity);
    if (byMinimum !== 0)
        return byMinimum;
    const byPrice = compare(left.unitPrice, right.unitPrice);
    if (byPrice !== 0)
        return byPrice;
    return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
};
export const resolvePrice = (input) => {
    const { list, items, variantId, quantity, at } = input;
    const unresolved = (reason, tiers = []) => ({
        applies: false,
        reason,
        variantId,
        quantity,
        at,
        list: list === null ? null : toResolvedList(list),
        tiers,
    });
    // A negative quantity is not a pricing question; answering it with the base
    // tier would be answering a different question than the one asked.
    if (isNegative(quantity))
        return unresolved('invalid-quantity');
    if (list === null)
        return unresolved('no-price-list');
    if (list.status !== 'active')
        return unresolved('price-list-archived');
    /*
     * A cor sem preço próprio segue o produto (F176). "Sem preço próprio" é
     * nenhum item ATIVO: o arquivado é o "voltar a seguir o produto".
     */
    const own = items.filter((item) => item.variantId === variantId);
    const fallback = input.fallbackVariantId ?? null;
    const follows = fallback !== null &&
        fallback !== variantId &&
        !own.some((item) => item.status === 'active');
    const forVariant = follows
        ? items.filter((item) => item.variantId === fallback)
        : own;
    if (forVariant.length === 0)
        return unresolved('no-price-for-variant');
    const ordered = [...forVariant].sort(mostSpecificFirst);
    const traces = [];
    let winner = null;
    for (const item of ordered) {
        const window = governingWindow(item, list);
        const trace = {
            itemId: item.id,
            minQuantity: item.minQuantity,
            unitPrice: item.unitPrice,
            windowSource: window.source,
            validFrom: window.from,
            validTo: window.to,
        };
        if (item.status !== 'active') {
            traces.push({ ...trace, outcome: 'archived' });
            continue;
        }
        // Rule 4 is applied by the sort, so the first candidate that clears every
        // other rule is the winner and the rest are recorded as superseded. That
        // ordering is what makes the trace readable top to bottom.
        if (compare(quantity, item.minQuantity) < 0) {
            traces.push({ ...trace, outcome: 'quantity-below-minimum' });
            continue;
        }
        const state = windowState(window.from, window.to, at);
        if (state !== 'in-force') {
            traces.push({ ...trace, outcome: state });
            continue;
        }
        if (winner === null) {
            winner = item;
            traces.push({ ...trace, outcome: 'applied' });
            continue;
        }
        traces.push({ ...trace, outcome: 'superseded-by-more-specific-tier' });
    }
    // Rule 5. Never a zero, never a silent fallback to some other list: a caller
    // that wanted the default list should have asked for the default list.
    if (winner === null)
        return unresolved('no-applicable-tier', traces);
    return {
        applies: true,
        variantId,
        quantity,
        at,
        list: toResolvedList(list),
        tiers: traces,
        unitPrice: winner.unitPrice,
        currency: list.currency,
        appliedItemId: winner.id,
        appliedMinQuantity: winner.minQuantity,
        basis: winner.minQuantity.units === 0n ? 'base-price' : 'quantity-tier',
        source: follows ? 'product' : 'own',
    };
};
/**
 * The tiers of a variant in a list, ordered for a person reading a table.
 *
 * Ascending by minimum, which is how the wholesale example in section 15 is
 * written and how anyone expects to see it — the opposite of the order the
 * resolver weighs them in.
 */
export const orderTiersForDisplay = (items) => [...items].sort((left, right) => compare(left.minQuantity, right.minQuantity));
/** Convenience for the `PriceList` row, which carries more than the resolver needs. */
export const listContext = (list) => ({
    id: list.id,
    code: list.code,
    name: list.name,
    currency: list.currency,
    isDefault: list.isDefault,
    validFrom: list.validFrom,
    validTo: list.validTo,
    status: list.status,
});
//# sourceMappingURL=resolution.js.map